<template>
  <ion-list class="dtc">
    <ion-item>
      <ion-toggle
        data-test="dtc-date"
        :checked="withDate"
        :disabled="withDate && !withTime"
        justify="space-between"
        @ion-change="setParts($event.detail.checked, withTime)"
      >Date</ion-toggle>
    </ion-item>
    <ion-item>
      <ion-toggle
        data-test="dtc-time"
        :checked="withTime"
        :disabled="withTime && !withDate"
        justify="space-between"
        @ion-change="setParts(withDate, $event.detail.checked)"
      >Time</ion-toggle>
    </ion-item>
    <ion-item>
      <ion-toggle
        data-test="dtc-now"
        :checked="modelValue.defaultNow"
        justify="space-between"
        @ion-change="setDefaultNow($event.detail.checked)"
      >Default to now</ion-toggle>
    </ion-item>
    <ion-item>
      <ion-select
        data-test="dtc-kind"
        label="Timezone"
        interface="popover"
        :value="kind"
        @ion-change="setKind($event.detail.value)"
      >
        <ion-select-option value="utc">UTC</ion-select-option>
        <ion-select-option value="local">Local (wall clock of the device)</ion-select-option>
        <ion-select-option value="zone">Named zone</ion-select-option>
        <ion-select-option value="offset">Fixed offset</ion-select-option>
      </ion-select>
    </ion-item>
    <ion-item v-if="kind === 'zone' || kind === 'offset'">
      <ion-input
        data-test="dtc-text"
        :label="kind === 'zone' ? 'Zone' : 'Offset'"
        label-placement="stacked"
        :placeholder="kind === 'zone' ? 'Europe/Berlin' : '+02:00'"
        autocapitalize="none"
        :value="text"
        @ion-input="setText(String($event.detail.value ?? ''))"
      />
    </ion-item>
    <ion-item v-if="textError" lines="none">
      <ion-label color="danger" class="ion-text-wrap" data-test="dtc-error">{{ textError }}</ion-label>
    </ion-item>
    <ion-item lines="none">
      <ion-label class="ion-text-wrap" color="medium">
        <p>{{ summary }}</p>
      </ion-label>
    </ion-item>
  </ion-list>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { IonInput, IonItem, IonLabel, IonList, IonSelect, IonSelectOption, IonToggle } from "@ionic/vue";
import type { DateTimeConfig } from "@/models/custom-fields";
import {
  formatFor,
  hasDate,
  hasTime,
  isValidTimezone,
  parseOffset,
  timezoneKind,
  type TimezoneKind,
} from "@/helper/datetime";

const props = defineProps<{ modelValue: DateTimeConfig }>();
const emit = defineEmits<{ (e: "update:modelValue", value: DateTimeConfig): void }>();

const withDate = computed(() => hasDate(props.modelValue.format));
const withTime = computed(() => hasTime(props.modelValue.format));

const kind = ref<TimezoneKind>(timezoneKind(props.modelValue.timezone));
const text = ref(kind.value === "zone" || kind.value === "offset" ? props.modelValue.timezone : "");
const textError = ref<string | null>(null);

function update(patch: Partial<DateTimeConfig>) {
  emit("update:modelValue", { ...props.modelValue, ...patch });
}

function setParts(date: boolean, time: boolean) {
  update({ format: formatFor(date, time) });
}

function setDefaultNow(enabled: boolean) {
  update({ defaultNow: enabled });
}

function setKind(next: TimezoneKind) {
  kind.value = next;
  textError.value = null;
  if (next === "utc" || next === "local") {
    text.value = "";
    update({ timezone: next });
    return;
  }
  text.value = next === "offset" ? "+00:00" : Intl.DateTimeFormat().resolvedOptions().timeZone;
  update({ timezone: text.value });
}

function setText(value: string) {
  text.value = value;
  const trimmed = value.trim();
  const invalid =
    kind.value === "offset"
      ? parseOffset(trimmed) === null && "Use an offset like +02:00 or -03:30."
      : !isValidTimezone(trimmed) && "Unknown zone, use a name like Europe/Berlin.";
  textError.value = invalid || null;
  if (!invalid) update({ timezone: trimmed });
}

const summary = computed(() => {
  const format = props.modelValue.format;
  const what = format === "date" ? "a date" : format === "time" ? "a time" : "a date and time";
  const timezone = props.modelValue.timezone;
  const where =
    timezone === "utc"
      ? "in UTC"
      : timezone === "local"
        ? "as the local wall clock, without an offset"
        : `in ${timezone}`;
  return `Stores ${what} ${where}${props.modelValue.defaultNow ? ", filled with now by default" : ""}.`;
});
</script>
